/**
 * Sportsbook — SABA Sports session-launch iframe host.
 *
 * The heavy lifting is on the backend (`/api/saba/session/launch`).
 * This page just:
 *   1. Reads the player's preferred currency from context.
 *   2. Calls the launch endpoint.
 *   3. Renders either the iframe or the appropriate fallback state
 *      (503 disabled / 403 KYC / 403 self-excluded / 400 currency / 401 / other).
 *   4. Listens for `postMessage` events from the SABA-hosted UI so we
 *      can resize the iframe or refetch the wallet on bet settlement.
 *
 * Header balance stays live: SABA is a seamless wallet — the same real
 * balance drains/credits on every bet. The existing top-bar wallet chip
 * already re-fetches `/api/auth/me` on route change AND on our
 * `sabaBalanceUpdate` postMessage below.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, ShieldCheck, Sparkles, Trophy } from "lucide-react";

import AppShell from "../components/ui-r1/AppShell";
import { Badge } from "../components/ui-r1/Badge";
import Button from "../components/ui-r1/Button";
import { Card, CardBody } from "../components/ui-r1/Card";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { sabaSessionLaunch } from "../lib/sabaApi";

// Allowed currency list surfaced to the "currency not supported" state.
// The backend also returns this via `/api/admin/saba/config` but only to
// admins — for the player-facing fallback we keep a static list so we
// never accidentally reveal a not-yet-enabled currency by fetching it.
const FALLBACK_ALLOWED = ["BDT", "INR", "PKR", "USDT"];

const IFRAME_MIN_HEIGHT_VH = 80;

export default function Sportsbook() {
  const { user, refresh } = useAuth();
  const { activeCurrency } = useCurrency();
  const currency = activeCurrency || user?.preferred_currency || "USDT";

  const [state, setState] = useState({ status: "loading" });
  const [iframeHeight, setIframeHeight] = useState(null); // px override from postMessage
  const iframeRef = useRef(null);

  // Attempt launch. Extracted so the "retry" button re-runs it.
  const launch = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const data = await sabaSessionLaunch({
        currency, language: "en", platform: "web",
      });
      setState({ status: "ready", launchUrl: data.launchUrl });
    } catch (err) {
      const httpStatus = err?.response?.status;
      const code = err?.response?.data?.detail?.errorCode
        || err?.response?.data?.errorCode
        || null;
      if (httpStatus === 401) {
        // Session lapsed — let the shared axios interceptor redirect.
        // Rendering "unknown" here just for visual completeness before nav.
        setState({ status: "error", code: "UNAUTHENTICATED" });
        return;
      }
      // Map every known errorCode into a discrete render branch.
      setState({ status: "error", httpStatus, code: code || "UNKNOWN" });
    }
  }, [currency]);

  useEffect(() => { launch(); }, [launch]);

  // Cross-frame messaging. SABA (or the placeholder host) can emit:
  //   { type: "sabaResize", height: <number> }
  //   { type: "sabaBalanceUpdate" }
  //
  // We accept messages from any origin because our sandbox base URL is
  // a placeholder — Phase 4 will lock this down to the real SABA
  // origin once credentials are onboarded. Meanwhile the strictest we
  // can be is a shape check.
  useEffect(() => {
    function onMessage(ev) {
      const msg = ev?.data;
      if (!msg || typeof msg !== "object") return;
      if (msg.type === "sabaResize" && typeof msg.height === "number") {
        // Clamp so a hostile / buggy remote can't push a 1e9px iframe
        // and lock up the browser.
        setIframeHeight(Math.max(400, Math.min(4000, msg.height)));
      } else if (msg.type === "sabaBalanceUpdate") {
        refresh?.();
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [refresh]);

  const heightStyle = useMemo(
    () => ({ minHeight: `${IFRAME_MIN_HEIGHT_VH}vh`, height: iframeHeight ? `${iframeHeight}px` : undefined }),
    [iframeHeight]
  );

  return (
    <AppShell>
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-6" data-testid="sportsbook-page">
        {/* Page header — matches the copy density of other authed routes */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Trophy className="w-5 h-5 text-gold" />
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-gold">Sportsbook</div>
              <h1 className="font-display italic text-2xl sm:text-3xl text-ink leading-tight">
                Live Sports & Pre-match
              </h1>
            </div>
            <Badge variant="gold" className="ml-2">Beta</Badge>
          </div>
        </div>

        {/* Body — one of loading / iframe / fallback */}
        {state.status === "loading" && <LoadingBlock />}
        {state.status === "ready" && (
          <Card className="overflow-hidden" data-testid="sportsbook-iframe-card">
            <iframe
              ref={iframeRef}
              src={state.launchUrl}
              title="SABA Sportsbook"
              className="w-full block bg-black"
              style={heightStyle}
              allow="fullscreen; payment"
              data-testid="sportsbook-iframe"
            />
          </Card>
        )}
        {state.status === "error" && (
          <FallbackForCode
            code={state.code}
            httpStatus={state.httpStatus}
            currency={currency}
            onRetry={launch}
          />
        )}
      </div>
    </AppShell>
  );
}


// --------------------------------------------------------------------------- //
// Sub-components
// --------------------------------------------------------------------------- //


function LoadingBlock() {
  return (
    <Card data-testid="sportsbook-loading">
      <CardBody className="flex items-center justify-center py-24 gap-3">
        <Loader2 className="w-5 h-5 text-gold animate-spin" />
        <span className="text-[10px] tracking-widest uppercase text-ink-3">
          Warming up your session…
        </span>
      </CardBody>
    </Card>
  );
}


function FallbackForCode({ code, httpStatus, currency, onRetry }) {
  if (code === "SPORTSBOOK_DISABLED") {
    return <ComingSoonBlock />;
  }
  if (code === "KYC_REQUIRED") {
    return <KycRequiredBlock />;
  }
  if (code === "SELF_EXCLUDED") {
    return <SelfExcludedBlock />;
  }
  if (code === "CURRENCY_NOT_SUPPORTED") {
    return <CurrencyNotSupportedBlock currency={currency} />;
  }
  // UNKNOWN / network / 500 etc.
  return <GenericErrorBlock httpStatus={httpStatus} onRetry={onRetry} />;
}


function ComingSoonBlock() {
  return (
    <Card
      className="border-gold/30 overflow-hidden"
      data-testid="sportsbook-coming-soon"
    >
      <CardBody className="relative py-16 sm:py-24 text-center">
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at top, rgba(200,167,101,0.6), transparent 60%)",
          }}
        />
        <div className="relative">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold/10 border border-gold/40 mb-5 shadow-glow-gold">
            <Trophy className="w-6 h-6 text-gold" />
          </div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-2">
            Coming soon
          </div>
          <h2 className="font-display italic text-3xl sm:text-4xl text-ink mb-3">
            Sportsbook launching soon
          </h2>
          <p className="text-sm text-ink-2 max-w-lg mx-auto mb-6">
            We&apos;re dialing in the odds. Full pre-match & live sports
            markets are minutes away — save the tab, come back this week.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/">
              <Button variant="secondary" data-testid="sportsbook-back-to-casino">
                <Sparkles className="w-4 h-4 mr-2" /> Back to Casino
              </Button>
            </Link>
            <Link to="/promotions">
              <Button variant="ghost" data-testid="sportsbook-view-promos">
                View bonuses
              </Button>
            </Link>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}


function KycRequiredBlock() {
  return (
    <Card className="border-line" data-testid="sportsbook-kyc-required">
      <CardBody className="py-16 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-highest border border-line mb-5">
          <ShieldCheck className="w-6 h-6 text-gold" />
        </div>
        <h2 className="font-display italic text-2xl text-ink mb-3">
          Verify your account to access Sports
        </h2>
        <p className="text-sm text-ink-2 max-w-md mx-auto mb-6">
          Sports betting requires a completed KYC. It takes about 2
          minutes — same document you&apos;d use for a withdrawal.
        </p>
        <Link to="/wallet/kyc">
          <Button variant="primary" data-testid="sportsbook-kyc-cta">
            Verify identity →
          </Button>
        </Link>
      </CardBody>
    </Card>
  );
}


function SelfExcludedBlock() {
  return (
    <Card className="border-line" data-testid="sportsbook-self-excluded">
      <CardBody className="py-16 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-highest border border-line mb-5">
          <ShieldCheck className="w-6 h-6 text-danger" />
        </div>
        <h2 className="font-display italic text-2xl text-ink mb-3">
          Sports is unavailable on this account
        </h2>
        <p className="text-sm text-ink-2 max-w-md mx-auto mb-6">
          This section is unavailable per your responsible-gaming
          settings.
        </p>
        <Link to="/responsible-gambling">
          <Button variant="secondary" data-testid="sportsbook-rg-link">
            Review responsible-gaming settings
          </Button>
        </Link>
      </CardBody>
    </Card>
  );
}


function CurrencyNotSupportedBlock({ currency }) {
  return (
    <Card className="border-line" data-testid="sportsbook-currency-unsupported">
      <CardBody className="py-16 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-highest border border-line mb-5">
          <AlertTriangle className="w-6 h-6 text-gold" />
        </div>
        <h2 className="font-display italic text-2xl text-ink mb-3">
          Sports isn&apos;t available for {currency} yet
        </h2>
        <p className="text-sm text-ink-2 max-w-md mx-auto mb-1">
          Supported currencies for sports betting:
        </p>
        <div className="flex items-center justify-center gap-2 flex-wrap mb-6">
          {FALLBACK_ALLOWED.map((c) => (
            <Badge key={c} variant="muted" className="!text-[10px]">
              {c}
            </Badge>
          ))}
        </div>
        <Link to="/wallet">
          <Button variant="secondary" data-testid="sportsbook-switch-currency">
            Manage wallet →
          </Button>
        </Link>
      </CardBody>
    </Card>
  );
}


function GenericErrorBlock({ httpStatus, onRetry }) {
  return (
    <Card className="border-line" data-testid="sportsbook-generic-error">
      <CardBody className="py-16 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-highest border border-line mb-5">
          <AlertTriangle className="w-6 h-6 text-danger" />
        </div>
        <h2 className="font-display italic text-2xl text-ink mb-3">
          Something went wrong loading Sports
        </h2>
        <p className="text-sm text-ink-2 max-w-md mx-auto mb-6">
          Please try again in a moment.{httpStatus ? ` (Error ${httpStatus})` : ""}
        </p>
        <Button variant="primary" onClick={onRetry} data-testid="sportsbook-retry-btn">
          Try again
        </Button>
      </CardBody>
    </Card>
  );
}
